import { getEnv } from '@unni/platform'

import AuthLogger from '@/features/oauth/utils/auth-logger'

export interface SignInAdaptor {
  getLoginURI(): string
}

export const kakao: SignInAdaptor = {
  getLoginURI(): string {
    try {
      const scope = ['account_email', 'birthday', 'name', 'phone_number', 'talk_message'].join(',')
      const url = new URL('https://kauth.kakao.com/oauth/authorize')
      const clientId = getEnv<string | undefined>('NEXT_PUBLIC_KAKAO_REST_API_KEY')
      const redirectUri = getEnv<string | undefined>('NEXT_PUBLIC_KAKAO_REDIRECT_URI')
      url.searchParams.set('response_type', 'code')
      url.searchParams.set('scope', scope)
      if (clientId) {
        url.searchParams.set('client_id', clientId)
      } else {
        throw new Error('kakao clientId is not set')
      }
      if (redirectUri) {
        url.searchParams.set('redirect_uri', redirectUri)
      } else {
        throw new Error('kakao redirectUri is not set')
      }

      const urlResult = url.toString()

      // Kakao OAuth 시작 로깅
      AuthLogger.step('Kakao OAuth 시작', {
        provider: 'kakao',
        redirectUri: redirectUri?.startsWith('http://') ? 'HTTP_WARNING' : 'HTTPS',
        hasClientId: !!clientId,
        scopeCount: scope.split(',').length,
      })

      return urlResult
    } catch (e) {
      console.error('kakao.getLoginURI', e)
      throw e
    }
  },
}

export const line: SignInAdaptor = {
  getLoginURI(): string {
    try {
      const LINE_STATE = Date.now().toString()
      const LINE_SCOPE = ['profile', 'openid', 'email'].join(' ')
      const channelId = getEnv<string | undefined>('NEXT_PUBLIC_LINE_CHANNEL_ID')
      const redirectUri = getEnv<string | undefined>('NEXT_PUBLIC_LINE_REDIRECT_URI')
      const url = new URL('https://access.line.me/oauth2/v2.1/authorize')
      url.searchParams.set('response_type', 'code')
      if (channelId) {
        url.searchParams.set('client_id', channelId)
      } else {
        throw new Error('line channelId is not set')
      }
      if (redirectUri) {
        url.searchParams.set('redirect_uri', redirectUri)
      } else {
        throw new Error('line redirectUri is not set')
      }
      url.searchParams.set('state', LINE_STATE)
      url.searchParams.set('scope', LINE_SCOPE)

      const urlResult = url.toString()

      // Line OAuth 시작 로깅
      AuthLogger.step('Line OAuth 시작', {
        provider: 'line',
        state: LINE_STATE,
        redirectUri: redirectUri?.startsWith('http://') ? 'HTTP_WARNING' : 'HTTPS',
        hasChannelId: !!channelId,
      })

      return urlResult
    } catch (e) {
      console.error('line.getLoginURI', e)
      throw e
    }
  },
}

export const fb: SignInAdaptor = {
  getLoginURI(): string {
    try {
      const FB_STATE = Date.now().toString()
      const url = new URL('https://www.facebook.com/v14.0/dialog/oauth')
      const appId = getEnv<string | undefined>('NEXT_PUBLIC_FB_APP_ID')
      const redirectUri = getEnv<string | undefined>('NEXT_PUBLIC_FB_REDIRECT_URI')

      url.searchParams.set('state', FB_STATE)
      url.searchParams.set('scope', 'email')
      if (appId) {
        url.searchParams.set('client_id', appId)
      } else {
        throw new Error('fb appId is not set')
      }
      if (redirectUri) {
        url.searchParams.set('redirect_uri', redirectUri)
      } else {
        throw new Error('fb redirectUri is not set')
      }
      return url.toString()
    } catch (e) {
      console.error('fb.getLoginURI', e)
      throw e
    }
  },
}

export const apple: SignInAdaptor = {
  getLoginURI(): string {
    try {
      const url = new URL('https://appleid.apple.com/auth/authorize')
      const clientId = getEnv<string | undefined>('NEXT_PUBLIC_APPLE_CLIENT_ID')
      const redirectUri = getEnv<string | undefined>('NEXT_PUBLIC_APPLE_REDIRECT_URI')
      const scope = ['name', 'email'].join(' ')

      url.searchParams.set('response_type', 'code id_token')
      url.searchParams.set('state', 'origin:web')
      url.searchParams.set('scope', scope)
      url.searchParams.set('response_mode', 'form_post')
      url.searchParams.set('m', '11')
      url.searchParams.set('v', '1.5.4')

      if (clientId) {
        url.searchParams.set('client_id', clientId)
      } else {
        throw new Error('apple clientId is not set')
      }
      if (redirectUri) {
        url.searchParams.set('redirect_uri', redirectUri)
      } else {
        throw new Error('apple redirectUri is not set')
      }
      return url.toString()
    } catch (e) {
      console.error('apple.getLoginURI', e)
      throw e
    }
  },
}

export const google: SignInAdaptor = {
  getLoginURI(): string {
    const qs = Object.entries({
      client_id: (getEnv('NEXT_PUBLIC_GOOGLE_CLIENT_ID') as string) || '',
      redirect_uri: (getEnv('NEXT_PUBLIC_GOOGLE_REDIRECT_URI') as string) || '',
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'consent',
      state: 'origin:web',
    })
      .map(([key, value]) => `${key}=${encodeURIComponent(value || '')}`)
      .join('&')

    return `https://accounts.google.com/o/oauth2/v2/auth?${qs}`
  },
}
